/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {disableScheduledReport, enableScheduledReport, runNowScheduledReport} from '@ion/core/src/services/Reports';
import {ScheduledReport} from '@ion/core/src/types/entities';
import Divider from '@ion/dls/src/components/divider/Divider';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {selectLoggedInUser} from '../../store/selectors/login';
import {
  setCreateScheduledReport,
  setDeletingScheduledReport,
  setDuplicatingScheduledReport,
  setEditingScheduledReport,
  setEditScheduledReport,
  setSelectedDashboardReport,
  setSelectedScheduledReport,
  setShowAddScheduledReportEditor
} from './store';

// defines the user row editor prop definitions ...
export interface ScheduledReportRowEditorTypes {
  data: ScheduledReport;
  isSchedulerDirector?: boolean;
  isSchedulerFleetManager?: boolean;
  onRefresh?: () => void;
}

// defines the styles used by this component ...
const useStyles = makeStyles(theme => ({
  root : {
    display   : 'block',
    '&:focus' : {
      outline : 'none'
    }
  },
  visibleDivider : {
    backgroundColor : dlsColor('grey3')
  }
}));

/**
 * Defines the ScheduledReportRowEditor component. This component contains the cell actions
 * for Scheduled Reports grid.
 *
 * ```typescript
 *
 *   import {ScheduledReportRowEditor} from './ScheduledReportRowEditor';
 *
 *   <ScheduledReportRowEditor .../>
 *
 * ```
 */
export const ScheduledReportRowEditor = ({
  data: scheduledReport,
  onRefresh,
  isSchedulerDirector = false,
  isSchedulerFleetManager = false
}: ScheduledReportRowEditorTypes) => {
  const classes               = useStyles();
  const cellMenuRef           = useRef(null);
  const loggedInUser          = useSelector(selectLoggedInUser);
  const enabled               = scheduledReport?.enabled ?? false;
  const currentUsername       = loggedInUser?.username?.toLowerCase();
  const ownerUsername         = scheduledReport?.owner?.username?.toLowerCase();
  const canManageReport       = !ownerUsername || currentUsername === ownerUsername;
  const enableText            = translate('sisense:analytics.menu.enable', {enabled: enabled ? 'Off' : 'On'});

  function closeMenu() {
    cellMenuRef?.current.closeCellMenu(cellMenuRef.current, true);
  };

  function editScheduledReport() {
    setSelectedDashboardReport({
      oid   : scheduledReport?.dashboard?.id,
      title : scheduledReport?.dashboard?.title
    });
    setEditingScheduledReport(true);
    setCreateScheduledReport(null);
    setEditScheduledReport(scheduledReport);
    setDuplicatingScheduledReport(false);
    setShowAddScheduledReportEditor(true);
    closeMenu();
  };

  function duplicateScheduledReport() {
    const {id, ...duplicatedReport} = scheduledReport || {};
    setCreateScheduledReport(duplicatedReport as ScheduledReport);
    setEditScheduledReport(null);
    setDuplicatingScheduledReport(true);
    setEditingScheduledReport(false);
    setSelectedDashboardReport({
      oid   : scheduledReport?.dashboard?.oid || scheduledReport?.dashboard?.id,
      title : scheduledReport?.dashboard?.title
    });
    setShowAddScheduledReportEditor(true);
    closeMenu();
  };

  function deleteScheduledReport() {
    setSelectedScheduledReport(scheduledReport);
    setDeletingScheduledReport(true);
    closeMenu();
  };

  async function enableDisableScheduledReport() {
    await (enabled ? disableScheduledReport(scheduledReport) : enableScheduledReport(scheduledReport));
    // refresh the grid in place
    onRefresh?.();
    closeMenu();
  };

  async function runScheduledReport() {
    await runNowScheduledReport(scheduledReport);
    // refresh the grid in place
    onRefresh?.();
    closeMenu();
  };

  function isActionEnabled() {
    return !!((isSchedulerFleetManager || canManageReport));
  };

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div className={classes.root}>
          <CellMenuItem icon={<MapEditIcon />}
            disabled={!isActionEnabled()}
            onClick={editScheduledReport}
            label={translate('sisense:analytics.menu.edit')} />
          <CellMenuItem icon={<SystemDeleteIcon />}
            disabled={!isActionEnabled()}
            onClick={deleteScheduledReport}
            label={translate('sisense:analytics.menu.delete')} />
          <Divider className={classes.visibleDivider} />
          <CellMenuItem disabled={!isActionEnabled()}
            onClick={enableDisableScheduledReport}
            label={enableText} />
          <Divider className={classes.visibleDivider} />
          <CellMenuItem disabled={!isActionEnabled()}
            onClick={runScheduledReport}
            label={translate('sisense:analytics.menu.run')} />
        </div>
      } />
    </>
  );
};
